<template>
  <router-view></router-view>
</template>
<script setup>
import { computed, onMounted, watch, onServerPrefetch } from "vue";
import { useRoute } from "vue-router";
import { setI18nLanguage, getCurrentLanguage } from "@storefront/core/i18n";
import config from "@config";
import { useStore } from "vuex";
import VueCookies from "vue-cookies";
import { useGtm } from "@gtm-support/vue-gtm";
import { useHead } from "@unhead/vue";
import { createToast } from "@storefront/core/helpers";
import { library } from "@fortawesome/fontawesome-svg-core";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { useI18n } from "vue-i18n";

library.add(far);
library.add(fas);
library.add(fab);

// Use store in setup
const { locale } = useI18n();
const store = useStore();
const gtm = useGtm();
const route = useRoute();
const generalMsg = computed(() => store.state.messages.generalMsg);
const isLoggedIn = computed(() => store.getters["user/getIsLoggedIn"]);

const contentList = ["footer_copyright", "footer_quick_links", "footer_contact", "banner"];

const sliders = [];

function getLanguageFromUrl(path) {
  // Split the path and get the first segment after the initial slash
  const segments = path.split("/").filter(Boolean); // filter(Boolean) removes empty strings

  if (segments.length === 0) {
    // URL is just "/" - return default language
    return config.languages.default;
  }

  const potentialLang = segments[0];

  // Check if the first segment is a valid language
  if (config.languages.available.includes(potentialLang)) {
    return potentialLang;
  }

  // First segment is not a language (e.g., /some-page) - return default
  return config.languages.default;
}

// Set up meta information using useHead
useHead({
  titleTemplate: `%s - ${config.app_name}`,
  htmlAttrs: { lang: store.getters["i18n/locale"] ?? locale.value },
  meta: [
    { charset: "utf-8" },
    { name: "viewport", content: "width=device-width, initial-scale=1" },
    { name: "google", content: "notranslate" },
  ],
  script: [
    {
      src: "https://cdn.consentmanager.net/delivery/js/semiautomatic.min.js",
      type: "text/javascript",
      "data-cmp-ab": "1",
      "data-cmp-cdid": "e39d19d862722",
      "data-cmp-host": "c.delivery.consentmanager.net",
      "data-cmp-cdn": "cdn.consentmanager.net",
      "data-cmp-codesrc": "0",
    },
  ],
});

// Watchers
watch(
  () => store.state.messages.msgItems.length,
  async () => {
    const msgs = await store.dispatch("messages/giveLastMessages");
    msgs.forEach((msg) => {
      createToast(msg);
    });
  }
);

watch(locale, () => {
  store.dispatch("faq/load"), store.dispatch("category_theme/loadCategory");
  store.dispatch("cmsBlock/multiple", { key: "identifier", value: contentList });
  store.dispatch("user/loadCountries");
  store.dispatch("forms/loadForms");
  store.dispatch("quote/loadQuoteRequestProducts", store);

  useHead({
    htmlAttrs: { lang: store.getters["i18n/locale"] ?? locale.value },
  });
});

async function fetchData() {
  store.dispatch("forms/loadForms");
  // store.dispatch("faq/load");
  const detectedLang = getLanguageFromUrl(route.path);
  setI18nLanguage(detectedLang);
  await Promise.allSettled([
    store.dispatch("messages/loadGeneralMsg"),
    store.dispatch("cmsPage/loadDefault", { id: "home" }),
    store.dispatch("sliders/multiple", { key: "identifier", value: sliders }),
    store.dispatch("menu/loadMenu"),
    store.dispatch("user/loadCountries"),
    store.dispatch("faq/load"),
    store.dispatch("category_theme/loadCategory"),
    store.dispatch("cmsBlock/multiple", { key: "identifier", value: contentList }),
  ]);
}

onServerPrefetch(fetchData);
function waitForCMP(callback) {
  // Als __cmp al beschikbaar is, direct uitvoeren
  if (typeof window.__cmp === "function") {
    var x = window.__cmp("getCMPData");
    callback(x);
    return;
  }

  // Anders elke 100ms checken tot het beschikbaar is (max 10 seconden)
  var attempts = 0;
  var interval = setInterval(function () {
    attempts++;

    if (typeof window.__cmp === "function") {
      clearInterval(interval);
      var x = window.__cmp("getCMPData");
      callback(x);
    }

    if (attempts >= 100) {
      clearInterval(interval);
      console.warn("CMP niet gevonden na 10 seconden.");
    }
  }, 100);
}

const handleConsentChange = (x) => {

  if (typeof x === "string" && typeof window.__cmp === "function") {
    x = window.__cmp("getCMPData");
  }

  if (x && "vendorConsents" in x) {
    if (x.vendorConsents["s905"]) {
      gtm.enable(true);
      gtm.debug(true);
    } else {
      gtm.enable(false);
      gtm.debug(false);
    }
  }
};

onMounted(() => {
  store.dispatch("user/loadUser", { store });
  const language = localStorage.getItem("lang");
  if (language) {
    setI18nLanguage(language);
  } else {
    setI18nLanguage(getCurrentLanguage());
    localStorage.setItem("lang", getCurrentLanguage());
  }
  store.dispatch("quote/loadQuoteRequestProducts", store);

  waitForCMP(function (x) {

    // listen consent changes and update gtm accordingly
    handleConsentChange(x);
    if (typeof window.__cmp === "function") {
      window.__cmp("addEventListener", ["consent", handleConsentChange, false], null);
    }
    
  });
});
</script>
